import { useEffect, useMemo } from 'react';
import { Select, SelectProps } from '@mantine/core';
import logger from '@ayocore/platform/common/helpers/logger';
import {
  useRepositoryOptions,
  type UseRepositoryOptionsParams,
} from '@ayocore/platform/client/hooks/useRepositoryOptions';
import classes from './RepositorySelect.module.css';
import type { RepoItem, RpcRepoClient } from '@ayocore/platform/server/rpc/types';

interface RepositorySelectProps<R extends RpcRepoClient>
  extends Omit<SelectProps, 'data' | 'loading'>,
    Omit<UseRepositoryOptionsParams<R>, 'repo'> {
  repo: R; // RPC-репозиторий (цепочка)
  displayField?: keyof RepoItem<R> & string;
  valueField?: keyof RepoItem<R> & string;
  getOptionLabel?: (item: RepoItem<R>) => string;
  getOptionValue?: (item: RepoItem<R>) => string | number;
  customFilter?: (items: RepoItem<R>[]) => RepoItem<R>[];

  // Server-side search options
  enableServerSearch?: boolean; // Auto-detect if repo has .search() method
  initialLimit?: number; // How many records to load initially (default: 50)
  searchLimit?: number; // How many search results to show (default: 50)
  searchDebounce?: number; // Debounce delay in ms (default: 300)
  minSearchLength?: number; // Minimum chars to trigger search (default: 2)
  /** Предзагруженная опция — гарантированно присутствует в data с первого рендера.
   *  Используется для предзаполненных значений, чтобы Mantine мог показать лейбл
   *  до загрузки полного списка (например, текущий пользователь в поле менеджера). */
  initialOption?: { value: string; label: string };
  /**
   * @deprecated Используйте `createRepositoryFilter` с опцией `without` (XOR-чекбокс
   * «Без X» в top-слоте) вместо sentinel-опции. Проп остаётся для обратной
   * совместимости single-фильтров до их миграции на фабрику.
   *
   * Статичные опции, не приходящие из репозитория — подмешиваются в начало списка
   * (напр. «— Без менеджера —» с sentinel-value для фильтра IS NULL). В отличие от
   * данных репозитория, не зависят от загрузки и не пропадают. Скрываются во время
   * активного серверного поиска по тексту. Обработку sentinel-value делает `onChange`. */
  staticOptions?: { value: string; label: string }[];
}

/**
 * RepositorySelect - Select component powered by repository
 *
 * Features:
 * - Работает с RPC-репозиториями (chainable query)
 * - Auto-detects display field (name, title, label)
 * - Supports custom option formatting
 * - Loading states
 * - Error handling
 * - Server-side search with debouncing
 *
 * Загрузка/поиск/маппинг инкапсулированы в useRepositoryOptions (общее ядро с
 * RepositoryCombobox). Компонент отвечает только за рендер Mantine Select и за
 * специфичную для single-select логику staticOptions.
 */
export function RepositorySelect<R extends RpcRepoClient>({
  repo,
  displayField,
  valueField,
  getOptionLabel,
  getOptionValue,
  customFilter,
  enableServerSearch = true,
  initialLimit = 50,
  searchLimit = 50,
  searchDebounce = 300,
  minSearchLength = 2,
  initialOption,
  staticOptions,
  ...selectProps
}: RepositorySelectProps<R>) {
  const { options, error, search, setSearch, hasServerSearch } = useRepositoryOptions<R>({
    repo,
    displayField,
    valueField,
    getOptionLabel,
    getOptionValue,
    customFilter,
    enableServerSearch,
    initialLimit,
    searchLimit,
    searchDebounce,
    minSearchLength,
    initialOption,
  });

  // Сбрасываем search когда внешнее value очищается программно
  // (иначе Mantine продолжает показывать имя предыдущего выбора)
  useEffect(() => {
    const isEmpty = selectProps.value == null || selectProps.value === '';
    if (isEmpty && search !== '') {
      setSearch('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectProps.value]);

  const selectData = useMemo(
    () => options.map(({ value, label }) => ({ value, label })),
    [options]
  );

  // Подмешиваем статичные опции в начало списка. Они не зависят от загрузки
  // репозитория, поэтому не пропадают. Прячем их во время активного поиска по
  // тексту, чтобы не мешали результатам.
  const dataWithStatic = useMemo(() => {
    if (!staticOptions || staticOptions.length === 0) return selectData;
    if (search.trim() !== '') return selectData;
    const staticValues = new Set(staticOptions.map(o => o.value));
    const dynamic = selectData.filter(opt => !staticValues.has(opt.value));
    return [...staticOptions, ...dynamic];
  }, [selectData, staticOptions, search]);

  if (error) {
    logger.warn('⚠️ RepositorySelect error, falling back to empty select');
  }

  return (
    <Select
      classNames={{ option: classes.option }}
      {...selectProps}
      value={
        selectProps.value == null || selectProps.value === '' ? null : String(selectProps.value)
      }
      data={dataWithStatic}
      disabled={selectProps.disabled}
      placeholder={selectProps.placeholder}
      error={error || selectProps.error}
      searchable={hasServerSearch || (selectProps.searchable ?? true)}
      filter={
        hasServerSearch
          ? ({ options, limit }) => options.slice(0, limit ?? options.length)
          : selectProps.filter
      }
      clearable={selectProps.clearable ?? true}
      searchValue={hasServerSearch ? search : undefined}
      onSearchChange={hasServerSearch ? setSearch : selectProps.onSearchChange}
      onChange={(value, option) => {
        if (selectProps.onChange) {
          selectProps.onChange(value == null ? null : String(value), option);
        }
      }}
    />
  );
}

export default RepositorySelect;
